import { useAppDispatch, useAppSelector } from 'appState/store'
import PayerVALogo from 'assets/images/PayerVALogo.svg'
import { NavLink, NavLinkCustom } from 'components/Layout'
import { OBDropdown } from '@outbound-ai/ursa'
import { authApi } from 'features/auth/authApi'
import { useCheckBatchStatus } from 'features/autocheck/autoCheckHooks'
import { CurrentUserLiveCallsDisplay } from 'features/calls/components/CurrentUserLiveCallsDisplay'
import { softphone } from 'features/calls/utils/softphone'
import { updateAllClaimsRoute } from 'features/claims/claimsSlice'
import { useUserProfile } from 'features/settings/settingsHooks'
import { IUserRoleGuidEnum } from 'features/settings/settingsTypes'
import { PathsEnum } from 'pages/Paths'
import { useCallback, useEffect } from 'react'
import { Nav } from 'react-bootstrap'
import { FaBolt, FaCog, FaEnvelope, FaExchangeAlt, FaLayerGroup, FaRegArrowAltCircleLeft } from 'react-icons/fa'
import { Link, useLocation, useNavigate } from 'react-router-dom'

import styled, { useTheme } from 'styled-components'
import { generateHSLString } from 'utils/helper'
import { notificator } from 'utils/notificator/notificator'
import { getTntIdForNavigation } from 'utils/tenantUrlUtils'

import { MAINTENANCE_STORAGE_KEY, OUTBOUND_SUPPORT } from 'utils/constants'
import { BatchNotification } from './Notification'
import { SELECTED_TENANT_UNAVAILABLE, TenantSelection } from './TenantSelection'

// assets
/*import GraphIcon from 'assets/images/icons/GraphIcon.svg'*/
// components
// props and utils
// features
export const Navbar = () => {
  const dispatch = useAppDispatch()
  const { user } = useAppSelector((state) => state.auth)
  const obGridParams = useAppSelector((state) => state.claims.claimsTable.params)
  const { data: profile } = useUserProfile(user?.id)
  const permission = profile?.roles.map((role) => role.roleId)
  const tenants = profile?.tenants
  const { isConnected } = useAppSelector((state) => state.calls.talkToPayorRepLiveCall)

  const { data: batchSettings } = useCheckBatchStatus()

  const batchProcessingSettings = batchSettings?.filter((setting: any) => setting.key === 'batchprocessorenabled')[0]

  const familyName = user?.family_name || ''
  const givenName = user?.given_name || ''

  const hsl = generateHSLString(givenName + familyName)
  // utils
  const navigate = useNavigate()
  const location = useLocation()
  const theme = useTheme()
  const tenantId = getTntIdForNavigation()

  useEffect(() => {
    const isTenantUnAvailable = sessionStorage.getItem(SELECTED_TENANT_UNAVAILABLE)

    if (isTenantUnAvailable) {
      notificator.notify({
        type: 'warn',
        heading: isTenantUnAvailable,
        toastOptions: {
          autoClose: 2000,
          closeButton: true,
          closeOnClick: false,
          onClose: () => {
            sessionStorage.removeItem(SELECTED_TENANT_UNAVAILABLE)
          },
          toastId: 'asdas-87asdfds-dfds',
        },
      })
      /* notificator.warn(isTenantUnAvailable, {
        autoClose: false,
        closeButton: true,
        closeOnClick: false,
        onClose: () => sessionStorage.removeItem(SELECTED_TENANT_UNAVAILABLE),
        toastId: 'asdas-87asdfds-dfds',
      }) */
    }
  }, [location.pathname])

  // handlers
  const handleClick = () => {
    ['currentPage', 'oaiClaimId', 'isRowSelected'].forEach((key) => localStorage.removeItem(key))
    deselectClaims()
    const tenantId = getTntIdForNavigation()
    if (permission && tenants?.length != 0) {
      permission?.includes(IUserRoleGuidEnum.claimAccessPermission) &&
        dispatch(updateAllClaimsRoute({ batchId: undefined }))

      navigate(
        permission?.includes(IUserRoleGuidEnum.claimAccessPermission)
          ? tenantId
            ? `/${tenantId}/claims`
            : '/claims'
          : permission?.includes(IUserRoleGuidEnum.batchUploadPermission)
          ? tenantId
            ? `/${tenantId}/batch`
            : '/batch'
          : permission?.includes(IUserRoleGuidEnum.fileSharePermission)
          ? tenantId
            ? `/${tenantId}/file-share`
            : '/file-share'
          : PathsEnum.home,
      )
    } else {
      navigate(PathsEnum.home)
    }
  }

  const handleNav = () => {
    // clear local storage
    ['currentPage', 'oaiClaimId', 'isRowSelected'].forEach((key) => localStorage.removeItem(key))
    deselectClaims()
    dispatch(updateAllClaimsRoute({ batchId: undefined }))
    navigate(PathsEnum.root)
  }

  const deselectClaims = () => {
    if (obGridParams) {
      obGridParams.api.deselectAll()
      // dispatch(updateSelectedRow({ root: ClaimTypesEnum.needsWork, selectedRow: [] }))
      const x = document.querySelector('#selectGridHeader') as HTMLInputElement
      if (x) {
        x.checked = false
      }
    }
  }

  const handleSignOut = useCallback(() => {
    authApi.signOut()
    if (isConnected) {
      softphone.muteAudio()
      softphone.muteMicrophone()
      softphone.disconnect()
    }
    localStorage.removeItem(MAINTENANCE_STORAGE_KEY)

    // dispatch(logout())
    // navigate(PathsEnum.root)
    // window.location.reload()
  }, [dispatch, isConnected])

  const openEmail = () => {
    window.open(`mailto:${OUTBOUND_SUPPORT}?subject=&body=`)

    // notificator.success(toastSuccessMessage)
  }

  const copyEmail = () => {
    if ('clipboard' in navigator) {
      navigator.clipboard.writeText(OUTBOUND_SUPPORT)
    } else {
      document.execCommand('copy', true, OUTBOUND_SUPPORT)
    }
  }

  // variables
  const avatarMenuItems = [
    {
      items: [
        {
          text: (
            <div className="avatar-menu avatar-menu-content settings">
              <FaCog className="ms-1" width="20" height="20" fill={theme.colors.info.darker} />
              <span style={{ marginLeft: '0.8rem' }}>Settings</span>
            </div>
          ),
          onClick: () =>
            navigate(
              tenantId
                ? `/${tenantId}/account/${PathsEnum.accountSettings}/${PathsEnum.accountSettingsProfile}`
                : `/account/${PathsEnum.accountSettings}/${PathsEnum.accountSettingsProfile}`,
            ),
        },
        ...(permission?.includes(IUserRoleGuidEnum.superAdmin)
          ? [
              {
                text: (
                  <div className="avatar-menu avatar-menu-content settings">
                    <FaCog className="ms-1" width="20" height="20" fill={theme.colors.info.darker} />
                    <span style={{ marginLeft: '0.8rem' }}>Organization Settings</span>
                  </div>
                ),
                onClick: () =>
                  navigate(
                    tenantId
                      ? `/${tenantId}/account/${PathsEnum.accountSettingsOrg}`
                      : `/account/${PathsEnum.accountSettingsOrg}`,
                  ),
              },
            ]
          : []),
        {
          text: (
            <div className="avatar-menu avatar-menu-content contact-us">
              <FaEnvelope className="ms-1" width="20" height="20" fill={theme.colors.info.darker} />
              <span style={{ marginLeft: '0.8rem' }}>Contact us</span>
            </div>
          ),
          onClick: openEmail,
        },
        {
          text: <hr className="my-1" />,
          onClick: () => undefined,
          disabled: true,
        },
        {
          text: (
            <div className="avatar-menu avatar-menu-content logout">
              <FaRegArrowAltCircleLeft width="20" height="20" className="ms-1" fill={theme.colors.error.neutral} />
              <span style={{ marginLeft: '0.8rem' }}>Log out</span>
            </div>
          ),
          onClick: handleSignOut,
        },
      ],
    },
  ]

  return (
    <StyledNavbar>
      <div>
        <NavLinkCustom testId="app-logo-link" onClick={handleClick} className="logo-container">
          <img className="logo" src={PayerVALogo} alt="Outbound AI Logo" />
        </NavLinkCustom>
      </div>

      <div className="navbar-links">
        <Nav>
          {permission?.includes(IUserRoleGuidEnum.fileSharePermission) && tenants?.length != 0 && (
            <Nav.Item>
              <Nav.Link
                to={getTntIdForNavigation() ? `/${getTntIdForNavigation()}/file-share` : '/file-share'}
                as={NavLink}
                onClick={() => {
                  ['currentPage', 'oaiClaimId', 'isRowSelected'].forEach((key) => localStorage.removeItem(key))
                  deselectClaims()
                }}
              >
                <FaExchangeAlt />
                File Share
              </Nav.Link>
            </Nav.Item>
          )}

          {batchProcessingSettings?.value !== 'false' &&
            permission?.includes(IUserRoleGuidEnum.batchUploadPermission) &&
            tenants?.length != 0 && (
              <Nav.Item>
                <Nav.Link
                  to={getTntIdForNavigation() ? `/${getTntIdForNavigation()}/batch` : '/batch'}
                  as={NavLink}
                  onClick={() => {
                    ['currentPage', 'oaiClaimId', 'isRowSelected'].forEach((key) => localStorage.removeItem(key))
                    deselectClaims()
                  }}
                >
                  <FaBolt />
                  Batch Processing
                </Nav.Link>
              </Nav.Item>
            )}
          {permission?.includes(IUserRoleGuidEnum.claimAccessPermission) && tenants?.length != 0 && (
            <Nav.Item>
              <Nav.Link
                to={getTntIdForNavigation() ? `/${getTntIdForNavigation()}/claims` : '/claims'}
                as={NavLink}
                onClick={handleNav}
              >
                <FaLayerGroup />
                Claims
              </Nav.Link>
            </Nav.Item>
          )}

          {/*<Nav.Item>
            <Nav.Link to={getTntIdForNavigation() ? `/${getTntIdForNavigation()}/analytics` : '/analytics'} as={NavLink}>
              <img src={GraphIcon} alt="GraphIcon" width="18" height="18" /> Analytics
            </Nav.Link>
          </Nav.Item>*/}
        </Nav>
      </div>

      <div className="navbar-right px-2">
        {permission?.includes(IUserRoleGuidEnum.claimAccessPermission) && tenants?.length != 0 && (
          <CurrentUserLiveCallsDisplay />
        )}

        <section className="header d-flex align-items-center justify-content-between">
          <TenantSelection />
        </section>
        {permission?.includes(IUserRoleGuidEnum.claimAccessPermission) && tenants?.length != 0 && <BatchNotification />}
        <div className="usermenu">
          <OBDropdown
            className="d-flex align-items-center gap-1"
            contenttype="icon-only"
            buttonName={
              familyName && givenName
                ? givenName?.charAt(0)?.toUpperCase() + '' + familyName?.charAt(0)?.toUpperCase()
                : 'N/A'
            }
            items={avatarMenuItems}
            variant="flat"
            radius="circle"
            hideCaret={true}
          />
        </div>
      </div>
    </StyledNavbar>
  )
}

const StyledNavbar = styled.nav`
  position: fixed;
  inset-inline: 0;
  top: 0;
  display: flex;
  align-items: center;
  background: ${({ theme }) => theme.colors.primary.darker};
  font-size: 1rem;
  white-space: nowrap;
  color: rgba(255, 255, 255, 0.56);
  z-index: ${({ theme }) => theme.zIndex.overlay};
  padding: 0 ${({ theme }) => theme.spacing.l};
  ${({ theme }) => theme.typography.m.normal};
  height: ${({ theme }) => theme.constants.navbarHeight};
  .logo-container {
    display: flex;
    justify-content: center;
    align-items: center;
    margin-right: 1rem;
    height: ${({ theme }) => theme.constants.navbarHeight};
    .logo {
      padding-right: ${({ theme }) => theme.util.pixelToRem(18)};
    }
  }

  .navbar-links {
    display: flex;
    flex: 1;
    height: 100%;
    .nav-item {
      div {
        margin: 0;
      }
    }

    .nav {
      flex-wrap: nowrap;
    }

    a {
      display: flex;
      align-items: center;
      justify-content: center;
      gap: 0.5rem;
      padding-block: 1.5rem;
      color: rgba(255, 255, 255, 0.56);
      min-width: 5rem;
      font-weight: ${({ theme }) => theme.fontWeight.medium};

      &:not(.active) {
        img {
          opacity: 0.6;
        }
      }

      &:not(.active):hover {
        color: ${({ theme }) => theme.colors.grey.lightest};

        img {
          filter: brightness(0) invert(1);
          opacity: 1;
        }
      }

      &.active {
        color: ${({ theme }) => theme.colors.grey.lightest};
        position: relative;

        &::after {
          content: '';
          position: absolute;
          display: block;
          bottom: 0;
          left: -0.1rem;
          right: -0.1rem;
          height: 0.375rem;
          background-color: ${({ theme }) => theme.colors.grey.lightest};
          border-radius: 0.5rem 0.5rem 0 0;
        }

        img {
          filter: brightness(0) invert(1);
        }
      }
    }

    .dropdown {
      .dropdown-menu {
        border-radius: 1rem;
        transform: translateY(${({ theme }) => theme.constants.navbarHeight}) !important;
        a {
          color: ${({ theme }) => theme.colors.grey.dark};
          &:active {
            background-color: ${({ theme }) => theme.colors.primary.light};
          }
        }
      }
    }
  }

  .navbar-right {
    display: flex;
    align-items: center;
    gap: ${({ theme }) => theme.spacing.s};
    color: ${({ theme }) => theme.colors.grey.lightest};
    cursor: pointer;
  }
  .avatar-menu {
    ${({ theme }) => theme.typography.s.normal};
    height: 2rem;
    padding-right: 0.5rem !important;
    padding-left: 1rem !important;
  }

  .avatar-menu-content {
    display: inline-flex;
    align-items: center;
    gap: 0.5rem;
    width: 100%;
  }

  .avatar-menu:hover {
    color: ${({ theme }) => theme.colors.neutral.dark};
    background-color: ${({ theme }) => theme.colors.grey.lightest};
  }

  .avatar-menu:focus {
    color: ${({ theme }) => theme.colors.neutral.dark};
    background-color: ${({ theme }) => theme.colors.grey.lighter};
  }

  .contact-us {
    margin-bottom: 0.5rem;
  }
  hr {
    margin: 0;
    border: 1px solid ${({ theme }) => theme.colors.grey.lighter};
  }
  .logout {
    margin-bottom: 0.25rem;
    margin-top: 0.5rem;
  }
  .settings {
    margin-top: 0.25rem;
  }
  .usermenu {
    .dropdown {
      /* .dropdown-toggle::after {
        display: none;
      } */
      .group-item {
        margin-bottom: 0;
      }
      .dropdown-menu {
        width: 15rem !important;
      }
      .dropdown-toggle::after {
        display: none;
      }
    }
  }
`
